import {
  Badge,
  Button,
  DataTable,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ScrollArea,
  ScrollBar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import {
  useCreateDistributorEmployee,
  useDistributorEmployeeById,
  useDistributorEmployees,
  useEmployeeRoles,
  useDeleteDistributorEmployee,
  useResendDistributorEmployeeInvite,
  useUpdateDistributorEmployee,
} from "creso-core/hooks";
import { Hourglass, MoreVerticalIcon } from "lucide-react";
import { TeamEmptyStateIcon } from "@core/components/icon/team-empty-state";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useAuthContext } from "@/providers/auth-provider";
import { handleError, handleFormValidationErrors } from "@/utils/error";
import capitalize from "lodash/capitalize";
import { useNavigate } from "@tanstack/react-router";
import {
  distributorArnFromUsername,
  getInitials,
  isEmployeeUser,
} from "creso-core/utils";
import { AvatarEmployee } from "web-core";
import {
  AddTeamMemberDialog,
  type AddTeamMemberFormValues,
  type InviteSuccessState,
} from "../components/add-team-member-dialog";
import { DeleteTeamMemberDialog } from "../components/delete-team-member-dialog";

type TeamMemberRow = {
  id: number;
  name: string;
  username: string;
  contact: string;
  email: string;
  role_id?: number;
  role: string;
  clientsAssigned?: string;
  status: "active" | "verification_pending" | "deactivated";
};

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Helps debugging in the browser console without breaking the page.
    // eslint-disable-next-line no-console
    console.error("TeamMembers ErrorBoundary:", error);
    this.setState({ error });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="p-4 text-sm text-destructive">
          Something crashed while rendering this section.{" "}
          {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

/** API `details` keys → react-hook-form names for AddTeamMemberFormValues */
const TEAM_MEMBER_API_FIELD_MAP: Record<string, keyof AddTeamMemberFormValues> =
  {
    username: "usernameSuffix",
    phone_number: "phone",
  };

export function TeamMembers() {
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<InviteSuccessState | null>(
    null
  );
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [memberIdPendingDelete, setMemberIdPendingDelete] = useState<
    number | null
  >(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data, isLoading, isError } = useDistributorEmployees({
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
    status: "active,verification_pending",
  });
  const { user } = useAuthContext();
  const isCurrentUserEmployee = isEmployeeUser(user.user_type);

  const {
    data: employeeRoles,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useEmployeeRoles();
  const { mutateAsync: createMember, isPending: isCreating } =
    useCreateDistributorEmployee();
  const { mutateAsync: updateMember, isPending: isUpdating } =
    useUpdateDistributorEmployee();
  const { mutateAsync: deleteMember, isPending: isDeleting } =
    useDeleteDistributorEmployee();
  const {
    mutateAsync: resendInvite,
    isPending: isResendingInvite,
    variables: resendInviteVariables,
  } = useResendDistributorEmployeeInvite();
  const {
    data: selectedEmployee,
    isLoading: isFetchingEmployee,
    isError: isEmployeeFetchError,
    error: employeeFetchError,
  } = useDistributorEmployeeById(selectedEmployeeId ?? undefined, {
    enabled: isEditingExisting && isAddMemberOpen && selectedEmployeeId != null,
  });

  const defaultEmployeeRoleId = useMemo(() => {
    const results = employeeRoles?.results;
    if (!results?.length) return undefined;
    const employee = results.find((r) => r.name.toUpperCase() === "EMPLOYEE");
    if (employee) return employee.id;
    const fallback = results.find((r) =>
      r.name.toUpperCase().includes("EMPLOYEE")
    );
    return fallback?.id;
  }, [employeeRoles?.results]);

  const arnNumber = distributorArnFromUsername(user.username);
  const usernamePrefix = `ARN-${arnNumber}.`;

  const form = useForm<AddTeamMemberFormValues>({
    defaultValues: {
      name: "",
      role_id: undefined,
      phone: "",
      email: "",
      usernameSuffix: "",
    },
  });

  useEffect(() => {
    if (defaultEmployeeRoleId == null) return;
    const current = form.getValues("role_id");
    if (current == null) {
      form.setValue("role_id", defaultEmployeeRoleId);
    }
  }, [defaultEmployeeRoleId, form]);

  const apiRows: TeamMemberRow[] =
    data?.results?.map((m) => ({
      id: m.id,
      name: m.name,
      username: m.username,
      contact: m.phone_number,
      email: m.email,
      role_id: m.role_id,
      role: "",
      clientsAssigned: m.num_clients_assigned ?? "0",
      status: m.status,
    })) ?? [];

  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    employeeRoles?.results?.forEach((r) => {
      map.set(Number(r.id), r.name);
    });
    return map;
  }, [employeeRoles?.results]);

  const rows: TeamMemberRow[] = apiRows.map((row, idx) => {
    const raw = data?.results?.[idx];
    const rawRoleId = raw?.role_id != null ? Number(raw.role_id) : null;

    const derivedRole =
      raw?.role_id != null
        ? (roleIdToName.get(rawRoleId as number) ?? "Employee")
        : "Employee";

    return { ...row, role: derivedRole };
  });

  const getUsernameSuffix = (username: string) => {
    const firstDotIndex = username.indexOf(".");
    if (firstDotIndex === -1) return username;
    return username.slice(firstDotIndex + 1);
  };

  const normalizePhoneForInput = (phone: string) => {
    const compactPhone = phone.replace(/\s/g, "");
    if (compactPhone.startsWith("+91")) {
      return compactPhone.slice(3);
    }
    return compactPhone;
  };

  const openEditMemberModal = useCallback((employeeId: number) => {
    setIsEditingExisting(true);
    setInviteSuccess(null);
    setSelectedEmployeeId(employeeId);
    setIsAddMemberOpen(true);
  }, []);

  const requestDeleteMember = useCallback((memberId: number) => {
    setMemberIdPendingDelete(memberId);
    setIsDeleteConfirmOpen(true);
  }, []);

  useEffect(() => {
    if (!selectedEmployee || !isEditingExisting) return;
    form.reset({
      name: selectedEmployee.name,
      role_id: selectedEmployee.role_id ?? defaultEmployeeRoleId,
      phone: normalizePhoneForInput(selectedEmployee.phone_number),
      email: selectedEmployee.email,
      usernameSuffix: getUsernameSuffix(selectedEmployee.username),
    });
  }, [defaultEmployeeRoleId, form, isEditingExisting, selectedEmployee]);

  useEffect(() => {
    if (!isEmployeeFetchError) return;
    handleError(employeeFetchError, "Failed to fetch member details");
  }, [employeeFetchError, isEmployeeFetchError]);

  const navigate = useNavigate();

  const columns: ColumnDef<TeamMemberRow>[] = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        minSize: 140,
        maxSize: 200,
        cell: ({ row }) => (
          <div className="flex items-center gap-3 py-1">
            <AvatarEmployee
              size="default"
              fallback={getInitials(row.original.name)}
            />
            {row.original.status === "verification_pending" ? (
              <span className="text-md font-normal text-left">
                {row.original.name}
              </span>
            ) : (
              <Button
                variant="link"
                className="h-auto p-0 text-md font-normal text-left cursor-pointer"
                onClick={() =>
                  navigate({
                    to: "/clients",
                    search: (prev) => {
                      const rest = { ...prev };
                      delete rest.manage_clients;
                      delete rest.assign_to_employee_id;
                      return {
                        ...rest,
                        filter_employee_id: row.original.id,
                      };
                    },
                  })
                }
              >
                {row.original.name}
              </Button>
            )}
            {row.original.status === "verification_pending" ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="custom"
                    className="text-tag-yellow-foreground bg-tag-yellow h-8 w-8 rounded-md justify-center shrink-0"
                  >
                    <Hourglass className="size-3 stroke-2" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  The employee is yet to accept the invite
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        ),
      },
      {
        header: "User name",
        accessorKey: "username",
        maxSize: 160,
        cell: ({ row }) => (
          <span className="text-md">{row.original.username}</span>
        ),
      },
      {
        header: "Contact",
        accessorKey: "contact",
        maxSize: 100,
        cell: ({ row }) => (
          <span className="text-md">{row.original.contact}</span>
        ),
      },
      {
        header: "Role",
        accessorKey: "role",
        maxSize: 100,
        cell: ({ row }) => (
          <Badge
            variant="custom"
            className={
              row.original.role === "ADMIN"
                ? "bg-tag-violet text-tag-violet-foreground"
                : "bg-tag-slate text-tag-slate-foreground"
            }
          >
            {capitalize(row.original.role.toLowerCase())}
          </Badge>
        ),
      },
      {
        header: "Clients assigned",
        accessorKey: "clientsAssigned",
        maxSize: 100,
        cell: ({ row }) => (
          <span className="text-md">{row.original.clientsAssigned}</span>
        ),
      },
      {
        id: "assign",
        header: "",
        minSize: 40,
        maxSize: 80,
        cell: ({ row }) => {
          if (row.original.status === "verification_pending") {
            return (
              <Button
                variant="link"
                className="p-0 h-auto text-sm font-medium cursor-pointer"
                disabled={
                  isResendingInvite &&
                  resendInviteVariables?.id === row.original.id
                }
                onClick={async () => {
                  const member = data?.results?.find(
                    (m) => m.id === row.original.id
                  );
                  if (!member) {
                    toast.error("Could not load member details.");
                    return;
                  }
                  try {
                    await resendInvite({
                      id: member.id,
                      payload: {
                        username: member.username,
                        role_id: member.role_id,
                        status: member.status,
                        email: member.email,
                        phone_number: member.phone_number,
                        euin: member.euin || null,
                        sif_euin: member.sif_euin || null,
                        name: member.name,
                      },
                    });
                    toast.success("Invite resent");
                  } catch (e) {
                    handleError(e, "Failed to resend invite");
                  }
                }}
              >
                Resend Invite
              </Button>
            );
          }

          return (
            <Button
              variant="link"
              className="p-0 h-auto text-sm font-medium cursor-pointer"
              onClick={() =>
                navigate({
                  to: "/clients",
                  search: (prev) => {
                    const rest = { ...prev };
                    delete rest.filter_employee_id;
                    return {
                      ...rest,
                      manage_clients: true,
                      assign_to_employee_id: row.original.id,
                    };
                  },
                })
              }
            >
              Manage Clients
            </Button>
          );
        },
      },
      {
        id: "menu",
        header: "",
        minSize: 44,
        maxSize: 44,
        cell: ({ row }) => {
          const canEdit = row.original.status === "verification_pending";
          const canDelete = !isCurrentUserEmployee;
          const hasMenuItems = canEdit || canDelete;

          if (!hasMenuItems) {
            return null;
          }

          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVerticalIcon className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && (
                    <DropdownMenuItem
                      onClick={() => openEditMemberModal(row.original.id)}
                    >
                      Edit
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem
                      className="text-destructive hover:text-destructive focus:bg-destructive/10 focus:text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive"
                      onClick={() => requestDeleteMember(row.original.id)}
                    >
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [
      data?.results,
      isCurrentUserEmployee,
      navigate,
      openEditMemberModal,
      requestDeleteMember,
      resendInvite,
      isResendingInvite,
      resendInviteVariables?.id,
    ]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    manualPagination: true,
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: data?.count,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  const resetAddMemberForm = useCallback(() => {
    form.reset({
      name: "",
      role_id: defaultEmployeeRoleId,
      phone: "",
      email: "",
      usernameSuffix: "",
    });
  }, [defaultEmployeeRoleId, form]);

  const handleAddMemberModalOpenChange = (isOpen: boolean) => {
    setIsAddMemberOpen(isOpen);
    if (isOpen) {
      if (!isEditingExisting && defaultEmployeeRoleId != null) {
        form.setValue("role_id", defaultEmployeeRoleId);
      }
      return;
    }
    setIsEditingExisting(false);
    setSelectedEmployeeId(null);
    setInviteSuccess(null);
    resetAddMemberForm();
  };

  const openAddMemberFresh = useCallback(() => {
    setIsEditingExisting(false);
    setSelectedEmployeeId(null);
    setInviteSuccess(null);
    setIsAddMemberOpen(true);
    if (defaultEmployeeRoleId != null) {
      form.setValue("role_id", defaultEmployeeRoleId);
    }
  }, [defaultEmployeeRoleId, form]);

  const closeAddMemberModal = useCallback(() => {
    setIsAddMemberOpen(false);
    setIsEditingExisting(false);
    setSelectedEmployeeId(null);
    setInviteSuccess(null);
    resetAddMemberForm();
  }, [resetAddMemberForm]);

  const confirmDeleteMember = useCallback(async () => {
    if (memberIdPendingDelete == null) return;
    try {
      await deleteMember(memberIdPendingDelete);
      toast.success("Member deleted");
      if (isEditingExisting && selectedEmployeeId === memberIdPendingDelete) {
        closeAddMemberModal();
      }
    } catch (e) {
      handleError(e, "Failed to delete member");
    } finally {
      setIsDeleteConfirmOpen(false);
      setMemberIdPendingDelete(null);
    }
  }, [
    closeAddMemberModal,
    deleteMember,
    isEditingExisting,
    memberIdPendingDelete,
    selectedEmployeeId,
  ]);

  const watchedName = form.watch("name");
  const watchedRoleId = form.watch("role_id");
  const watchedPhone = form.watch("phone");
  const watchedEmail = form.watch("email");
  const watchedUsernameSuffix = form.watch("usernameSuffix");

  const selectedRoleForForm = employeeRoles?.results?.find(
    (role) => role.id === watchedRoleId
  );

  const computedNewUsername = watchedUsernameSuffix
    ? `${usernamePrefix}${watchedUsernameSuffix}`
    : "";

  const computedNewPhoneNumberNormalized = watchedPhone
    ? watchedPhone.replace(/\s/g, "")
    : "";

  const hasFormChanges =
    selectedEmployee != null &&
    (selectedEmployee.name !== watchedName ||
      Number(selectedEmployee.role_id) !== Number(watchedRoleId) ||
      normalizePhoneForInput(selectedEmployee.phone_number) !==
        computedNewPhoneNumberNormalized ||
      selectedEmployee.email !== watchedEmail ||
      selectedEmployee.username !== computedNewUsername);

  const editFormIsValid =
    isEditingExisting &&
    selectedEmployee != null &&
    watchedName &&
    watchedPhone &&
    watchedEmail &&
    watchedUsernameSuffix &&
    watchedRoleId != null &&
    selectedRoleForForm != null;

  const saveButtonDisabled =
    isCreating ||
    isFetchingEmployee ||
    isUpdating ||
    (isEditingExisting ? !editFormIsValid || !hasFormChanges : false);

  const onSubmit = async (values: AddTeamMemberFormValues) => {
    if (isEditingExisting) {
      if (selectedEmployee == null) {
        toast.error("Member details not loaded yet.");
        return;
      }
      if (
        !values.name ||
        !values.phone ||
        !values.email ||
        !values.usernameSuffix
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      if (values.role_id == null) {
        toast.error("Please select a role.");
        return;
      }

      const selectedRole = employeeRoles?.results?.find(
        (role) => role.id === values.role_id
      );

      if (!selectedRole) {
        toast.error("Please select a valid role.");
        return;
      }

      const newUsername = `${usernamePrefix}${values.usernameSuffix}`;
      const newPhoneNumber = `+91${values.phone.replace(/\s/g, "")}`;

      const hasChanges =
        selectedEmployee.name !== values.name ||
        Number(selectedEmployee.role_id) !== Number(values.role_id) ||
        normalizePhoneForInput(selectedEmployee.phone_number) !==
          values.phone.replace(/\s/g, "") ||
        selectedEmployee.email !== values.email ||
        selectedEmployee.username !== newUsername;

      if (!hasChanges) {
        toast.info("No changes to update.");
        return;
      }

      try {
        await updateMember({
          id: selectedEmployee.id,
          payload: {
            username: newUsername,
            role_id: values.role_id,
            status: selectedEmployee.status,
            email: values.email,
            phone_number: newPhoneNumber,
            euin: selectedEmployee.euin ?? null,
            sif_euin: selectedEmployee.sif_euin ?? null,
            name: values.name,
          },
        });

        toast.success("Member updated");
        closeAddMemberModal();
      } catch (e) {
        if (
          !handleFormValidationErrors(
            form,
            e,
            "",
            TEAM_MEMBER_API_FIELD_MAP as Record<string, string>
          )
        ) {
          handleError(e, "Failed to update member");
        }
      }
      return;
    }

    try {
      if (
        !values.name ||
        !values.phone ||
        !values.email ||
        !values.usernameSuffix
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      if (values.role_id == null) {
        toast.error("Please select a role.");
        return;
      }

      const selectedRole = employeeRoles?.results?.find(
        (role) => role.id === values.role_id
      );

      const createdMember = await createMember({
        name: values.name,
        role_id: values.role_id,
        phone_number: `+91${values.phone.replace(/\s/g, "")}`,
        email: values.email,
        username: `${usernamePrefix}${values.usernameSuffix}`,
      });
      toast.success("Member added");
      setInviteSuccess({
        id: createdMember.id,
        name: values.name,
        role: selectedRole?.name
          ? capitalize(selectedRole.name.toLowerCase())
          : "Employee",
        username: `${usernamePrefix}${values.usernameSuffix}`,
        phone: values.phone.replace(/\s/g, ""),
        email: values.email,
      });
    } catch (e) {
      if (
        !handleFormValidationErrors(
          form,
          e,
          "",
          TEAM_MEMBER_API_FIELD_MAP as Record<string, string>
        )
      ) {
        handleError(e, "Failed to add member");
      }
    }
  };

  return (
    <div className="p-5 w-full flex flex-col h-full">
      <div className="flex justify-between">
        <h4>Team members</h4>
        <AddTeamMemberDialog
          open={isAddMemberOpen}
          onOpenChange={handleAddMemberModalOpenChange}
          onAddMemberButtonClick={openAddMemberFresh}
          form={form}
          inviteSuccess={inviteSuccess}
          isEditingExisting={isEditingExisting}
          employeeRoles={employeeRoles}
          isRolesLoading={isRolesLoading}
          isRolesError={isRolesError}
          isCurrentUserEmployee={isCurrentUserEmployee}
          usernamePrefix={usernamePrefix}
          isCreating={isCreating}
          isFetchingEmployee={isFetchingEmployee}
          isUpdating={isUpdating}
          saveButtonDisabled={saveButtonDisabled}
          onSubmit={onSubmit}
          onClose={closeAddMemberModal}
          onEditFromSuccess={openEditMemberModal}
        />
      </div>

      <DeleteTeamMemberDialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          setIsDeleteConfirmOpen(open);
          if (!open) setMemberIdPendingDelete(null);
        }}
        onConfirm={confirmDeleteMember}
        isDeleting={isDeleting}
      />

      <div className="h-px w-full bg-border mt-3" />

      <div className="mt-4 flex-1">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : isError ? (
          <div className="text-sm text-destructive">
            Failed to load team members.
          </div>
        ) : rows.length > 0 ? (
          <div className="w-full overflow-hidden border border-border rounded-lg">
            <ScrollArea className="w-full">
              <ErrorBoundary>
                <DataTable
                  table={table}
                  className="border-0 min-w-max"
                  pagination={false}
                />
              </ErrorBoundary>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <DataTablePagination table={table} />
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-6 rounded-xl border border-dashed border-border px-6 py-16">
            <div
              className="flex shrink-0 items-center justify-center"
              aria-hidden
            >
              <TeamEmptyStateIcon />
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <p className="text-md text-foreground">
                Add team members and assign them clients to manage.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
